import {
  ChevronUpIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useDebouncedCallback } from '@superfunnel/react-hooks/useDebouncedCallback';
import {
  ConditionIf,
  FormField,
  HtmlStepperField,
  StepCondition,
} from '@superfunnel/types';
import Autocomplete from '@superfunnel/ui-core/Autocomplete';
import ChipsAutocomplete from '@superfunnel/ui-core/ChipsAutocomplete';
import Button from '@superfunnel/ui-core/v2/Button';
import Input from '@superfunnel/ui-core/v2/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import { ReactNode, useState } from 'react';
import { uid } from 'uid';
import {
  getHiddenOperatorInputType,
  hiddenOperators,
  numberOperatorFields,
  numberOperators,
  selectOperatorFields,
  selectOperators,
  textOperatorFields,
  textOperators,
} from '../utils/form-utils';

/** Field type that works with both FormField and HtmlStepperField */
type AnyField = FormField | HtmlStepperField;

/** Normalized field option for the condition editor UI */
interface FieldOption {
  id: string;
  label: string; // Computed label (from field.label or field.id)
  type: FormField['type'] | HtmlStepperField['type'];
  subtitle?: string;
  options?: { value: string; label: string }[];
}

/** Helper to get display label for a field */
const getFieldLabel = (field: AnyField): string => {
  if ('label' in field && field.label) {
    return field.label;
  }
  return field.id;
};

/** Helper to normalize a field to a FieldOption */
const normalizeField = (field: AnyField, subtitle?: string): FieldOption => {
  // Normalize options to always have a label
  let options: { value: string; label: string }[] | undefined;
  if ('options' in field && field.options) {
    options = field.options.map((opt) => {
      if (typeof opt === 'string') {
        return { value: opt, label: opt };
      }
      return { value: opt.value, label: opt.label ?? opt.value };
    });
  }

  return {
    id: field.id,
    label: getFieldLabel(field),
    type: field.type,
    subtitle,
    options,
  };
};

/**
 * Represents a step that can be used in conditions.
 * Works with both MultiFieldStepperStep and HtmlStepperStep.
 */
export interface StepWithFields {
  id: string;
  title?: string;
  name?: string;
  fields?: AnyField[];
  conditions?: StepCondition[];
}

/**
 * Represents a final step option for the "Go to" selector.
 */
export interface FinalStepOption {
  id: string;
  title?: string;
  name?: string;
}

export interface StepConditionEditorProps {
  /** The current step being edited */
  currentStep: StepWithFields;
  /** All steps in the form (for field options and step selection) */
  allSteps: StepWithFields[];
  /** Final steps available for navigation */
  finalSteps: FinalStepOption[];
  /** Index of the current step in allSteps */
  currentStepIndex: number;
  /** Callback when conditions change */
  onConditionsChange: (conditions: StepCondition[]) => void;
  /** Function to extract display text from step title (handles Quill format) */
  getStepDisplayName?: (
    step: StepWithFields | FinalStepOption | null,
  ) => string;
  /** Optional function to render a step thumbnail */
  renderStepThumbnail?: (stepId: string, isFinalStep: boolean) => ReactNode;
  /** Current default next step ID (empty or undefined = sequential next) */
  defaultNextStep?: string;
  /** Callback when the default next step changes */
  onDefaultNextStepChange?: (stepId: string) => void;
}

/**
 * A shared component for editing step conditions.
 * Can be used with both MultiFieldStepper and HtmlStepper forms.
 */
export const StepConditionEditor = ({
  currentStep,
  allSteps,
  finalSteps,
  currentStepIndex,
  onConditionsChange,
  getStepDisplayName = (step) => step?.name || step?.title || 'Step',
  renderStepThumbnail,
  defaultNextStep,
  onDefaultNextStepChange,
}: StepConditionEditorProps) => {
  const conditions = currentStep.conditions as StepCondition[] | undefined;
  const currentStepFields = currentStep.fields ?? [];

  // Build the list of all available fields from all steps (normalized to FieldOption)
  const buildFieldOptions = (): FieldOption[] => {
    const currentStepOptions = currentStepFields.map((f) =>
      normalizeField(f, 'Current Step'),
    );

    const otherStepOptions = allSteps
      .filter((s) => s.id !== currentStep.id)
      .flatMap((s) =>
        (s.fields ?? []).map((f) =>
          normalizeField(f, `Step ${s.name || s.title || s.id}`),
        ),
      );

    return [...currentStepOptions, ...otherStepOptions];
  };

  const handleAddCondition = () => {
    const defaultField = currentStepFields[0]?.id ?? '';
    const nextStepId = allSteps[currentStepIndex + 1]?.id ?? '';

    onConditionsChange([
      ...(conditions ?? []),
      {
        id: uid(),
        if: [
          {
            id: uid(),
            field: defaultField,
            operator: 'equals',
            value: null,
          },
        ],
        then: {
          step: nextStepId,
        },
      },
    ]);
  };

  const handleDeleteCondition = (conditionId: string) => {
    onConditionsChange((conditions ?? []).filter((c) => c.id !== conditionId));
  };

  const handleUpdateConditionIf = (
    conditionId: string,
    conditionIfId: string,
    update: Partial<ConditionIf>,
  ) => {
    onConditionsChange(
      (conditions ?? []).map((c) => {
        if (c.id === conditionId) {
          return {
            ...c,
            if: c.if.map((i) =>
              i.id === conditionIfId ? { ...i, ...update } : i,
            ),
          };
        }
        return c;
      }),
    );
  };

  const handleUpdateConditionThen = (conditionId: string, stepId: string) => {
    onConditionsChange(
      (conditions ?? []).map((c) => {
        if (c.id === conditionId) {
          return {
            ...c,
            then: { step: stepId },
          };
        }
        return c;
      }),
    );
  };

  const handleUpdateConditionName = useDebouncedCallback(
    (conditionId: string, name: string) => {
      onConditionsChange(
        (conditions ?? []).map((c) => {
          if (c.id === conditionId) {
            const { name: _, ...rest } = c;
            return name ? { ...rest, name } : rest;
          }
          return c;
        }),
      );
    },
    400,
  );

  const handleAddConditionRule = (conditionId: string) => {
    const defaultField = currentStepFields[0]?.id ?? '';

    onConditionsChange(
      (conditions ?? []).map((c) => {
        if (c.id === conditionId) {
          return {
            ...c,
            if: [
              ...c.if,
              {
                id: uid(),
                field: defaultField,
                operator: 'equals',
                value: null,
              },
            ],
          };
        }
        return c;
      }),
    );
  };

  const handleDeleteConditionRule = (
    conditionId: string,
    ruleIndex: number,
  ) => {
    onConditionsChange(
      (conditions ?? []).map((c) => {
        if (c.id === conditionId) {
          return {
            ...c,
            if: c.if.filter((_, i) => i !== ruleIndex),
          };
        }
        return c;
      }),
    );
  };

  const fieldOptions = buildFieldOptions();

  // Track which branches are expanded (all expanded by default)
  const [expandedBranches, setExpandedBranches] = useState<Set<string>>(
    () => new Set(conditions?.map((c) => c.id) ?? []),
  );

  const toggleBranch = (conditionId: string) => {
    setExpandedBranches((prev) => {
      const next = new Set(prev);
      if (next.has(conditionId)) {
        next.delete(conditionId);
      } else {
        next.add(conditionId);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Add Logic button */}
      <Button
        variant="outline"
        onClick={handleAddCondition}
        className="flex h-10 w-full items-center justify-center gap-2.5 text-[15px] font-medium"
      >
        <PlusIcon className="size-5" />
        Add Logic
      </Button>

      {/* Branch conditions */}
      {conditions?.map((cond, condIndex) => {
        const isExpanded = expandedBranches.has(cond.id);

        return (
          <div
            key={cond.id}
            className="flex flex-col gap-1 overflow-hidden rounded-xl border border-gray-200 bg-white pt-2 shadow-sm"
          >
            {/* Branch header */}
            <div className="flex items-start justify-between px-2.5 pb-1">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleBranch(cond.id)}
                  className="flex items-center gap-1 px-0"
                >
                  <ChevronUpIcon
                    className={`size-4.5 text-gray-500 transition-transform ${
                      isExpanded ? '' : 'rotate-180'
                    }`}
                  />
                </Button>
                <Input
                  type="text"
                  defaultValue={cond.name ?? ''}
                  onChange={(e) =>
                    handleUpdateConditionName(cond.id, e.target.value)
                  }
                  placeholder={`Branch ${condIndex + 1}`}
                  className="h-7 w-full border-0 text-sm font-medium text-gray-700 shadow-none ring-0 placeholder:text-gray-400 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:ring-offset-transparent"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteCondition(cond.id);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <TrashIcon className="size-4.5" />
              </Button>
            </div>

            {/* Expandable content */}
            {isExpanded && (
              <div className="flex flex-col gap-2 px-3 pb-3">
                {/* Separator */}
                <div className="h-px w-full bg-gray-200" />

                {/* Condition rules (IF statements) */}
                {cond.if.map((condIf, ifIndex) => {
                  const field = fieldOptions.find((f) => f.id === condIf.field);

                  return (
                    <div key={condIf.id} className="flex flex-col gap-2">
                      {/* If label for first rule only */}
                      {ifIndex === 0 && (
                        <p className="text-sm font-medium text-gray-700">If</p>
                      )}

                      {/* Vertical stack: Field, Operator, Value */}
                      <div className="flex flex-col gap-2">
                        {/* Field selector */}
                        <div className="flex flex-col gap-1">
                          <Autocomplete<FieldOption>
                            modal
                            size="thin"
                            selected={field}
                            options={fieldOptions}
                            getValue={(o) => o.label}
                            getGroup={(o) => o.subtitle}
                            getOptionId={(o) => o.id}
                            onChange={(f) => {
                              handleUpdateConditionIf(cond.id, condIf.id, {
                                field: f?.id ?? '',
                              });
                            }}
                          />
                        </div>

                        {/* Operator selector */}
                        <div className="flex flex-col gap-1">
                          {/* Text field operators */}
                          {textOperatorFields.includes(
                            field?.type ?? 'text',
                          ) && (
                            <Select
                              value={condIf.operator}
                              onValueChange={(o) => {
                                handleUpdateConditionIf(cond.id, condIf.id, {
                                  operator: o,
                                });
                              }}
                            >
                              <SelectTrigger size="thin">
                                <SelectValue placeholder="Operator" />
                              </SelectTrigger>
                              <SelectContent>
                                {textOperators.map((o) => (
                                  <SelectItem key={o.value} value={o.value}>
                                    {o.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}

                          {/* Number field operators */}
                          {numberOperatorFields.includes(
                            field?.type ?? 'text',
                          ) && (
                            <Select
                              value={condIf.operator}
                              onValueChange={(o) => {
                                handleUpdateConditionIf(cond.id, condIf.id, {
                                  operator: o,
                                });
                              }}
                            >
                              <SelectTrigger size="thin">
                                <SelectValue placeholder="Operator" />
                              </SelectTrigger>
                              <SelectContent>
                                {numberOperators.map((o) => (
                                  <SelectItem key={o.value} value={o.value}>
                                    {o.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}

                          {/* Hidden field operators */}
                          {field?.type === 'hidden' && (
                            <Select
                              value={condIf.operator}
                              onValueChange={(o) => {
                                handleUpdateConditionIf(cond.id, condIf.id, {
                                  operator: o,
                                  value: null,
                                });
                              }}
                            >
                              <SelectTrigger size="thin">
                                <SelectValue placeholder="Operator" />
                              </SelectTrigger>
                              <SelectContent>
                                <div className="px-2 py-1.5 text-xs font-medium text-gray-500">
                                  Text
                                </div>
                                {hiddenOperators
                                  .filter((o) => o.group === 'text')
                                  .map((o) => (
                                    <SelectItem key={o.value} value={o.value}>
                                      {o.label}
                                    </SelectItem>
                                  ))}
                                <div className="px-2 py-1.5 text-xs font-medium text-gray-500">
                                  Number
                                </div>
                                {hiddenOperators
                                  .filter((o) => o.group === 'number')
                                  .map((o) => (
                                    <SelectItem key={o.value} value={o.value}>
                                      {o.label}
                                    </SelectItem>
                                  ))}
                                <div className="px-2 py-1.5 text-xs font-medium text-gray-500">
                                  Date
                                </div>
                                {hiddenOperators
                                  .filter((o) => o.group === 'date')
                                  .map((o) => (
                                    <SelectItem key={o.value} value={o.value}>
                                      {o.label}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          )}

                          {/* Select field operators */}
                          {field &&
                            selectOperatorFields.includes(field.type) && (
                              <Select
                                value={condIf.operator}
                                onValueChange={(o) => {
                                  handleUpdateConditionIf(cond.id, condIf.id, {
                                    operator: o,
                                  });
                                }}
                              >
                                <SelectTrigger size="thin">
                                  <SelectValue placeholder="Operator" />
                                </SelectTrigger>
                                <SelectContent>
                                  {selectOperators.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>
                                      {o.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                        </div>

                        {/* Value input */}
                        <div className="flex flex-col gap-1">
                          {/* Text value */}
                          {textOperatorFields.includes(field?.type ?? 'text') &&
                            condIf.operator !== 'isEmpty' && (
                              <Input
                                placeholder="Value"
                                size="thin"
                                value={condIf.value ?? ''}
                                onChange={(e) => {
                                  handleUpdateConditionIf(cond.id, condIf.id, {
                                    value: e.target.value,
                                  });
                                }}
                              />
                            )}

                          {/* Number value */}
                          {numberOperatorFields.includes(
                            field?.type ?? 'text',
                          ) && (
                            <Input
                              placeholder="Value"
                              type="number"
                              size="thin"
                              value={condIf.value ?? ''}
                              onChange={(e) => {
                                handleUpdateConditionIf(cond.id, condIf.id, {
                                  value: e.target.valueAsNumber,
                                });
                              }}
                            />
                          )}

                          {/* Hidden field values */}
                          {field?.type === 'hidden' && (
                            <>
                              {getHiddenOperatorInputType(condIf.operator) ===
                                'text' && (
                                <Input
                                  placeholder="Value"
                                  size="thin"
                                  value={condIf.value ?? ''}
                                  onChange={(e) => {
                                    handleUpdateConditionIf(
                                      cond.id,
                                      condIf.id,
                                      {
                                        value: e.target.value,
                                      },
                                    );
                                  }}
                                />
                              )}
                              {getHiddenOperatorInputType(condIf.operator) ===
                                'number' && (
                                <Input
                                  placeholder="Value"
                                  type="number"
                                  size="thin"
                                  value={condIf.value ?? ''}
                                  onChange={(e) => {
                                    handleUpdateConditionIf(
                                      cond.id,
                                      condIf.id,
                                      {
                                        value: e.target.valueAsNumber,
                                      },
                                    );
                                  }}
                                />
                              )}
                              {getHiddenOperatorInputType(condIf.operator) ===
                                'date' && (
                                <Input
                                  placeholder="Date"
                                  type="date"
                                  size="thin"
                                  value={condIf.value ?? ''}
                                  onChange={(e) => {
                                    handleUpdateConditionIf(
                                      cond.id,
                                      condIf.id,
                                      {
                                        value: e.target.value,
                                      },
                                    );
                                  }}
                                />
                              )}
                            </>
                          )}

                          {/* Select field values */}
                          {field &&
                            selectOperatorFields.includes(field.type) && (
                              <>
                                {condIf.operator !== 'isEmpty' &&
                                  condIf.operator !== 'equals' &&
                                  condIf.operator !== 'notEquals' && (
                                    <ChipsAutocomplete<{
                                      value: string;
                                      label: string;
                                    }>
                                      modal
                                      size="thin"
                                      getValue={(c) => c?.label}
                                      selected={(field.options ?? []).filter(
                                        (o) => {
                                          if (
                                            condIf.value &&
                                            Array.isArray(condIf.value)
                                          ) {
                                            return (
                                              condIf.value as {
                                                value: string;
                                              }[]
                                            ).find((v) => v.value === o.value);
                                          }
                                          return false;
                                        },
                                      )}
                                      onChange={(s) => {
                                        handleUpdateConditionIf(
                                          cond.id,
                                          condIf.id,
                                          {
                                            value: s,
                                          },
                                        );
                                      }}
                                      getOptionId={(t) => t.value}
                                      options={field.options ?? []}
                                    />
                                  )}
                                {(condIf.operator === 'equals' ||
                                  condIf.operator === 'notEquals') && (
                                  <Select
                                    value={condIf.value as string}
                                    onValueChange={(o) => {
                                      handleUpdateConditionIf(
                                        cond.id,
                                        condIf.id,
                                        {
                                          value: o,
                                        },
                                      );
                                    }}
                                  >
                                    <SelectTrigger size="thin">
                                      <SelectValue placeholder="Select value" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {(field.options ?? []).map((o) => (
                                        <SelectItem
                                          key={o.value}
                                          value={o.value}
                                        >
                                          {o.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                              </>
                            )}

                          {/* Delete rule button for subsequent rules */}
                          {ifIndex > 0 && (
                            <Button
                              size="icon-sm"
                              color="negative"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteConditionRule(cond.id, ifIndex);
                              }}
                            >
                              <TrashIcon className="size-4" />
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* AND connector with Add Rule button */}
                      <div className="flex items-center gap-2 py-0.5">
                        <div className="h-px flex-1 bg-gray-200" />
                        {ifIndex === cond.if.length - 1 ? (
                          <Button
                            variant="outline"
                            size="micro"
                            onClick={() => handleAddConditionRule(cond.id)}
                          >
                            + Add Rule
                          </Button>
                        ) : (
                          <span className="text-sm text-gray-500">AND</span>
                        )}
                        <div className="h-px flex-1 bg-gray-200" />
                      </div>
                    </div>
                  );
                })}

                {/* Then -> Go to */}
                <div className="flex flex-col gap-2">
                  <p className="text-sm font-medium text-gray-700">
                    Then. Go to:
                  </p>
                  <Autocomplete<StepWithFields | FinalStepOption | null>
                    modal
                    size="thin"
                    selected={
                      [...allSteps, ...finalSteps].find(
                        (s) => s.id === cond.then.step,
                      ) ?? null
                    }
                    options={[null, ...allSteps, ...finalSteps].filter(
                      (s) => s?.id !== currentStep.id,
                    )}
                    getValue={(o) => (o ? getStepDisplayName(o) : 'Next Step')}
                    getOptionId={(o) => o?.id ?? 'next-step'}
                    getIcon={(o) => {
                      if (!o) {
                        return (
                          <span className="flex size-5 shrink-0 items-center justify-center rounded bg-gray-100 text-xs font-medium text-gray-500">
                            →
                          </span>
                        );
                      }
                      const stepIndex = allSteps.findIndex(
                        (s) => s.id === o.id,
                      );
                      if (stepIndex !== -1) {
                        return (
                          <span className="bg-brand-100 text-brand-700 flex size-5 shrink-0 items-center justify-center rounded text-xs font-semibold">
                            {stepIndex + 1}
                          </span>
                        );
                      }
                      // Final step
                      return (
                        <span className="flex size-5 shrink-0 items-center justify-center rounded bg-green-100 text-xs font-medium text-green-700">
                          ✓
                        </span>
                      );
                    }}
                    placeholder="Next Step"
                    onChange={(s) => {
                      handleUpdateConditionThen(cond.id, s?.id ?? '');
                    }}
                  />
                  {/* Step thumbnail preview */}
                  {renderStepThumbnail &&
                    cond.then.step &&
                    renderStepThumbnail(
                      cond.then.step,
                      finalSteps.some((s) => s.id === cond.then.step),
                    )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Default case */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="rounded-lg bg-gray-100 px-1.5 py-0.5 text-sm font-medium text-gray-900">
              {conditions?.length ? 'All other cases' : 'All Cases'}
            </span>
            <span className="text-sm font-medium text-gray-700">Go to:</span>
          </div>
          {onDefaultNextStepChange ? (
            <Autocomplete<StepWithFields | FinalStepOption | null>
              modal
              size="thin"
              selected={
                defaultNextStep
                  ? ([...allSteps, ...finalSteps].find(
                      (s) => s.id === defaultNextStep,
                    ) ?? null)
                  : null
              }
              options={[null, ...allSteps, ...finalSteps].filter(
                (s) => s?.id !== currentStep.id,
              )}
              getValue={(o) => (o ? getStepDisplayName(o) : 'Next Step')}
              getOptionId={(o) => o?.id ?? 'next-step'}
              getIcon={(o) => {
                if (!o) {
                  return (
                    <span className="flex size-5 shrink-0 items-center justify-center rounded bg-gray-100 text-xs font-medium text-gray-500">
                      →
                    </span>
                  );
                }
                const stepIndex = allSteps.findIndex((s) => s.id === o.id);
                if (stepIndex !== -1) {
                  return (
                    <span className="bg-brand-100 text-brand-700 flex size-5 shrink-0 items-center justify-center rounded text-xs font-semibold">
                      {stepIndex + 1}
                    </span>
                  );
                }
                // Final step
                return (
                  <span className="flex size-5 shrink-0 items-center justify-center rounded bg-green-100 text-xs font-medium text-green-700">
                    ✓
                  </span>
                );
              }}
              placeholder="Next Step"
              onChange={(s) => {
                onDefaultNextStepChange(s?.id ?? '');
              }}
            />
          ) : (
            <span className="text-base text-gray-900">
              {defaultNextStep
                ? getStepDisplayName(
                    [...allSteps, ...finalSteps].find(
                      (s) => s.id === defaultNextStep,
                    ) ?? null,
                  )
                : 'Next Step'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
